import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useState } from "react";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { WalletService } from "../../services/WalletService";

interface InputProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    userId: number;
    userWallet: { id: number; amount: string } | null;
    /** Called after a successful settlement so the parent can refetch the wallet. */
    onSettled?: () => void;
}

const walletService = new WalletService();

const SettleNegativeWalletBalanceDialog = (props: InputProps) => {
    const [notes, setNotes] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const balance = Number(props.userWallet?.amount ?? 0);
    const hasNegativeBalance = !!props.userWallet && balance < 0;
    const settlementAmount = Math.abs(balance).toFixed(2);
    const isFormValid = hasNegativeBalance && notes.trim() !== "";

    const handleClose = () => {
        if (submitting) return;
        setNotes("");
        props.setOpen(false);
    };

    const handleSubmit = async () => {
        if (!isFormValid) return;
        setSubmitting(true);
        try {
            await walletService.settleNegativeWalletBalance(props.userId, notes.trim());
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: `₹${settlementAmount} credited. The customer's wallet is now at 0.00 and they can book again.`,
                type: CustomSnackbarTypes.SUCCESS,
            });
            setNotes("");
            props.setOpen(false);
            props.onSettled?.();
        } catch (e) {
            // HttpClient error interceptor already surfaces the snackbar.
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={props.open} onClose={handleClose} scroll={"paper"} fullWidth maxWidth={"sm"}>
            <DialogTitle>Settle Negative Wallet Balance</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {!hasNegativeBalance ? (
                        <Alert severity="info">
                            This customer has no negative balance to settle. Current wallet balance is{" "}
                            <b>₹{balance.toFixed(2)}</b>.
                        </Alert>
                    ) : (
                        <>
                            <TextField
                                label="Current wallet balance"
                                value={`₹${balance.toFixed(2)}`}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                            <TextField
                                label="Credit that will be added"
                                value={`₹${settlementAmount}`}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                            <TextField
                                label="Balance after settlement"
                                value={"₹0.00"}
                                InputProps={{ readOnly: true }}
                                fullWidth
                            />
                            <TextField
                                label="Reason for settling"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="e.g. waived after cook no-show on CHEFIT #12345"
                                required
                                multiline
                                minRows={2}
                                fullWidth
                                autoFocus
                            />
                            <Alert severity="warning">
                                The credit is always exactly the outstanding debt, so the wallet lands on ₹0.00 and the
                                customer gets no spendable balance. The debt will no longer be collected on their next
                                booking, and the customer is sent a &quot;₹{settlementAmount} added to your wallet&quot;
                                notification. This cannot be undone from here.
                            </Alert>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color={"success"}
                    onClick={handleSubmit}
                    size={"large"}
                    disabled={!isFormValid || submitting}
                >
                    {submitting ? "Settling..." : "Settle"}
                </Button>
                <Button
                    variant="contained"
                    color={"error"}
                    onClick={handleClose}
                    size={"large"}
                    disabled={submitting}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SettleNegativeWalletBalanceDialog;
